import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import GoogleSignInButton from "../components/GoogleSignInButton";
import Logo from "../components/Logo";
import { toast } from "react-toastify";

export default function SignupPage() {
  const { signup, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleGoogleCredential = async (credential: string) => {
    setError(null);
    setSubmitting(true);
    try {
      await loginWithGoogle(credential);
      toast.success("Welcome to Invoice Studio.");
      // The route guard sends users with no organization to the picker.
      navigate("/documents", { replace: true });
    } catch (err: any) {
      setError(err?.message || "Unable to sign up with Google");
      toast.error(err?.message || "Unable to sign up with Google");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await signup(name, email, password);
      toast.success("Account created. Welcome to Invoice Studio.");
      // New accounts have no organization yet — the guard routes to the picker.
      navigate("/select-organization", { replace: true });
    } catch (err: any) {
      setError(err?.message || "Unable to sign up");
      toast.error(err?.message || "Unable to sign up");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-shell flex min-h-screen items-center justify-center px-6 py-12">
      <div className="auth-glow auth-glow-one" />
      <div className="auth-glow auth-glow-two" />
      <Link to="/" className="absolute left-6 top-6 z-10">
        <Logo className="text-xl text-slate-900" />
      </Link>
      <div className="auth-card relative z-10 w-full max-w-md p-8 sm:p-10">
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-teal-700">A smarter way to invoice</p>
        <h1 className="font-display text-2xl font-semibold text-ink">Create your account</h1>
        <p className="mt-1 text-sm text-slate-500">Start creating invoices in minutes.</p>
        <form className="mt-6 grid gap-4" onSubmit={handleSubmit}>
          <label className="grid gap-1 text-sm font-medium text-slate-600">
            Name
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
            />
          </label>
          <label className="grid gap-1 text-sm font-medium text-slate-600">
            Email
            <input
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </label>
          <label className="grid gap-1 text-sm font-medium text-slate-600">
            Password
            <input
              type="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button type="submit" className="button" disabled={submitting}>
            {submitting ? "Creating account…" : "Sign up"}
          </button>
        </form>
        <div className="mt-4">
          <GoogleSignInButton
            onCredential={handleGoogleCredential}
            onError={(message) => setError(message)}
          />
        </div>
        <p className="mt-4 text-sm text-slate-500">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-brand">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
